/**
 * NoInternetBanner — sticky top banner shown while connectivity is lost.
 *
 * Flutter source of truth: `noInternetConnectivityWidget`
 * (`chat_screen.dart:3745-3768`).
 *
 * Behavior in Flutter:
 *  - `Visibility(visible: !hasInternetConnectivity, ...)` — the caller already
 *    gates rendering. This component does not own connectivity state; it
 *    only owns the banner shell. The parent decides whether to mount it.
 *  - Full-width container with `AppColors.black` background.
 *  - 12px padding on all sides.
 *  - Centered `AutoSizeText` "Checking For Internet Connection" at 15pt in
 *    `AppColors.activeRed`.
 *
 * Web port:
 *  - Renders a single sticky-positioned div that the chat page mounts at the
 *    top of its content stack. Stickiness is owned by the caller's layout —
 *    this component renders a plain block; the chat page's flex column does
 *    the placement (matching how the Flutter parent slots
 *    `noInternetConnectivityWidget()` directly into its `Column`).
 *  - Background uses `theme.black`; text uses `theme.activeRed`. No hardcoded
 *    color literals.
 *  - Padding uses `responsive.spacing.base` (= 12px, exact Flutter parity).
 *  - Font uses `responsive.font.lg` (= 16px on mobile, scaling on larger
 *    breakpoints) instead of locking to 15px — keeps the typography on the
 *    project's responsive scale per `presentation.md`.
 *  - The Flutter `AutoSizeText` `minFontSize: 15` behavior is not ported: web
 *    text shrinks via container width rather than the auto-size library, and
 *    the copy is short enough that a single line at the responsive lg size
 *    fits comfortably. Marked as a deliberate UI deviation per `presentation.md`.
 *
 * l10n: `chat.checkingForInternet` (sourced from
 *       `useLocale().l10n.chat.checkingForInternet`).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const NoInternetBanner: React.FC = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const chat = l10n.chat;

  return (
    <div
      {...qaAttr({ id: 'chat-no-internet-banner' })}
      style={{
        width: '100%',
        backgroundColor: theme.black,
        padding: responsive.spacing.base,
        boxSizing: 'border-box',
        textAlign: 'center',
      }}
    >
      <span
        style={{
          color: theme.activeRed,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.regular,
        }}
      >
        {chat.checkingForInternet}
      </span>
    </div>
  );
};
